import { rangeToResampleMapping } from '@chart';
import { Button } from '@design/components/button/Button';
import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import { ButtonLink } from '@design/components/button-link/ButtonLink';
import { RevampTable } from '@design/components/table';
import { Tooltip } from '@design/components/tooltip/Tooltip';
import { Filter } from '@terminal/components/filter/Filter';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { ScenariosData } from '@terminal/react-query-hooks/scenarios/useScenarios';
import { useMultipleSnakesMeta } from '@terminal/react-query-hooks/snakes/useMultipleSnakesMeta';
import { appPaths } from '@terminal/routes/app-paths';
import { PAST_EPISODES_HORIZONS } from '@terminal/widgets/past-episodes-widget/components/insight-past-episodes/InsightPastEpisodes';
import {
  getColumns,
  getHorizonsLabels,
  mapEpisodesToTableRow,
} from '@terminal/widgets/past-episodes-widget/components/insight-past-episodes/utils/utils';
import { PastEpisodesChart } from '@terminal/widgets/past-episodes-widget/components/past-episodes-chart/PastEpisodesChart';
import {
  DEFAULT_EPISODES_TO_SHOW,
  EMPTY_FILTER_LABELS,
  useCollapsibleEpisodesTable,
} from '@terminal/widgets/past-episodes-widget/hooks/useCollapsibleEpisodesTable';
import styles from '@terminal/widgets/past-episodes-widget/PastEpisodesWidget.module.scss';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router';

export interface ScenarioPastEpisodeSectionProps {
  snakes: string[];
  scenario: ScenariosData;
}

export const ScenarioPastEpisodeSection = ({
  scenario,
  snakes,
}: ScenarioPastEpisodeSectionProps) => {
  const { t } = useTranslation(['scenario', 'widget', 'chart']);
  const navigate = useNavigate();
  const [activeHorizonIdx, setActiveHorizonIdx] = useState(5);
  const {
    isCollapsed,
    tableRef,
    activeSortOrderOption,
    setActiveSortOrderOption,
    sortOrderFilter,
    handleViewMoreClick,
  } = useCollapsibleEpisodesTable();

  const { data, isFetching } = useMultipleSnakesMeta(snakes);

  const handleChangeHorizon = (index: number) => setActiveHorizonIdx(index);

  const handleFormatAssetsParams = () => {
    if (!data || data.length === 0) {
      return [];
    }

    return data.map((meta, index) => `${meta.entity};${meta.snake};${index}`);
  };

  const assets = isFetching
    ? undefined
    : data.map((meta, index) => ({
        tag: meta.entity,
        snake: meta.snake,
        paneIndex: index,
      }));

  const totalTradingDays =
    scenario.episodes.reduce((acc, ep) => acc + ep.events.length, 0) || 0;

  const formattedEpisodes = useMemo(
    () =>
      scenario.episodes
        .map(episode => {
          const endDate = episode.events.reduce((latest, event) => {
            return event.date > latest.date ? event : latest;
          }).date;

          return {
            start_date: episode.date,
            end_date: endDate,
            events: episode.events,
          };
        })
        .sort((a, b) =>
          activeSortOrderOption.value.id === 'asc'
            ? a.start_date.localeCompare(b.start_date)
            : b.start_date.localeCompare(a.start_date)
        ),
    [scenario.episodes, activeSortOrderOption.value.id]
  );

  const columns = getColumns(totalTradingDays, t);

  const episodesToShow = isCollapsed
    ? formattedEpisodes.slice(0, DEFAULT_EPISODES_TO_SHOW)
    : formattedEpisodes;

  return (
    <>
      <h2 className={styles.title} data-testid="scenario-past-episode">
        <span>
          <span>{t('widget:pastEpisode:title.occurrences')}</span>
          <span className={styles.pastEpisodeKey} />
          <TooltipContainer
            label={t('analyze:experiment.chart.tooltip')}
            showUnderline
          >
            <span>
              {t('widget:pastEpisode:title.episodes', {
                episodes: scenario.episodes.length,
              })}
            </span>
          </TooltipContainer>
          <span>
            {t('widget:pastEpisode:title.days', {
              totalTradingDays,
            })}
          </span>
        </span>
      </h2>
      <div className={styles.pastEpisodesButtons}>
        <ButtonGroup
          className={styles.styledButtonGroup}
          data-active-index={activeHorizonIdx}
          labels={getHorizonsLabels(t, PAST_EPISODES_HORIZONS)}
          onClick={handleChangeHorizon}
          activeIndex={activeHorizonIdx}
        />
        <Tooltip label={t('chart:fullscreen')}>
          <Button
            className={styles.styledButton}
            aria-label={t('chart:fullscreen')}
            iconName="Expand"
            variant="empty"
            size="small"
            onClick={() => {
              navigate({
                pathname: appPaths.chart,
                search: createSearchParams({
                  asset: handleFormatAssetsParams(),
                  horizon: PAST_EPISODES_HORIZONS[activeHorizonIdx],
                  primary: snakes,
                  resample:
                    rangeToResampleMapping[
                      PAST_EPISODES_HORIZONS[activeHorizonIdx]
                    ],
                  'yAxisType.1': 'merged',
                }).toString(),
              });
            }}
          />
        </Tooltip>
      </div>
      <div className={styles.chartWrapper}>
        <PastEpisodesChart
          assets={assets}
          episodes={formattedEpisodes}
          horizon={PAST_EPISODES_HORIZONS[activeHorizonIdx]}
        />
      </div>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('widget:pastEpisode.pastEpisode')}</h2>
        <Filter
          filter={sortOrderFilter}
          labels={EMPTY_FILTER_LABELS}
          filterOptions={[activeSortOrderOption]}
          addFilterOption={({ option }) => setActiveSortOrderOption(option)}
          removeFilterOption={() => {}}
          onCustomRangeClick={() => {}}
          contentStyle={{
            minWidth: 'var(--radix-dropdown-menu-trigger-width)',
          }}
        />
      </div>
      <>
        <div
          className={clsx(styles.tableRoot, {
            [styles.expanded]: !isCollapsed,
          })}
          ref={tableRef}
        >
          <RevampTable
            columnsMeta={columns}
            data={mapEpisodesToTableRow(episodesToShow)}
            showBorder={false}
            stickyHeader
            withBodyRowHover={false}
          />
        </div>
        {formattedEpisodes.length > DEFAULT_EPISODES_TO_SHOW && (
          <ButtonLink
            className={styles.viewMoreButton}
            label={t(
              `widget:pastEpisode.table.${
                isCollapsed ? 'viewMore' : 'viewLess'
              }`
            )}
            onClick={handleViewMoreClick}
          />
        )}
      </>
    </>
  );
};
